import Loading from 'components/shared/Loading';
import ReportsTabs from './ReportsTabs';
import { useReportTypes } from './useReportTypes';
import { MigrationOptions } from 'client-migration/clientMigration.types';
import { isPartiallyMigratedCdh, isPartiallyMigratedCobra, shouldHide } from 'shared/utils/migration-utils';
import { useMigrationStatus } from 'shared/custom-hooks/useMigrationStatus';
import { useSelector } from 'react-redux';
import { selectSelectedCompany } from 'companies/companies.slice';
import { Alert } from '@mui/material';

function Reports() {
  const reportTypes = useReportTypes();
  const { hasMigrated, status } = useMigrationStatus();
  const selectedCompany = useSelector(selectSelectedCompany);
  const partiallyMigrated = isPartiallyMigratedCobra(status, hasMigrated, selectedCompany)
    || isPartiallyMigratedCdh(status, hasMigrated, selectedCompany);

  const getPostMigrationMessage = (): string => {
    if (!status) {
      return '';
    }

    const cobraMigrationDate = !status.cobra_actual_migration_date ? null
      : new Date(status.cobra_actual_migration_date);
    const cdhMigrationDate = !status.cdh_actual_migration_date ? null
      : new Date(status.cdh_actual_migration_date);

    if (cobraMigrationDate && cdhMigrationDate && cobraMigrationDate.getDate() !== cdhMigrationDate.getDate()) {
      return `The reports listed below are reflective of activity that occurred prior to
        ${cobraMigrationDate.toLocaleDateString()} for COBRA/Direct Bill and ${cdhMigrationDate.toLocaleDateString()}
        for all others.`;
    } else {
      return `The reports listed below are reflective of activity that occurred prior to 
        ${cobraMigrationDate?.toLocaleDateString() ?? cdhMigrationDate?.toLocaleDateString()}.`;
    }
  };

  const getPartiallyMigratedMessage = (): string => {
    if (!status) {
      return '';
    }

    if (hasMigrated.cobra) {
      const cobraMigrationDate = !status.cobra_actual_migration_date ? null
        : new Date(status.cobra_actual_migration_date);

      return `The COBRA/Direct Bill reports listed below are reflective of activity that occurred prior to
        ${cobraMigrationDate?.toLocaleDateString()}.`;
    } else {
      const cdhMigrationDate = !status.cdh_actual_migration_date ? null
        : new Date(status.cdh_actual_migration_date);

      return `All non-COBRA/Direct Bill reports listed below are reflective of activity that occurred prior to
        ${cdhMigrationDate?.toLocaleDateString()}.`;
    }
  };

  return (
    <div className="w-full flex flex-col p-4">
      <h2 className="page-title">Reports</h2>
      {(shouldHide(status, hasMigrated, MigrationOptions.both, selectedCompany) || partiallyMigrated) && (
        <Alert severity="info" sx={ { mb: 3 } }>
          {partiallyMigrated ? getPartiallyMigratedMessage() : getPostMigrationMessage()}
        </Alert>
      )}
      <div className="w-full flex">
        {
          reportTypes !== null
            ? <ReportsTabs reportTypes={reportTypes} />
            : <Loading text="Loading report types" />
        }
      </div>
    </div>
  );
}

export default Reports;
